import type {
  AppID,
  Facility,
  FacilityGroupID,
  FacilityID,
  PermissionID,
  PermissionTypeID,
  SimpleSelectable,
  TenantID,
  UserID,
} from '../cross-app-shared/types/db';
import type { Email, Uid } from '../cross-app-shared/types/common';
import type { EmissionsMassUnitID } from '../cross-app-shared/types/emissions-mass-units';
import type { AppAccessTarget } from '../cross-app-shared/services/app-access';
import type { TenantCreateDraft } from '../cross-app-shared/services/tenants';
import type { WorkerAvatarLook } from '../cross-app-shared/types/worker-avatar';
import { crossAppBackendRequest } from './cross-app-backend-request';

export const crossAppActions = {
  users: {
    you() {
      return crossAppBackendRequest('you', {});
    },

    youUpdate(args: { name: string }) {
      return crossAppBackendRequest('youUpdate', args);
    },

    youAvatarUpdate(args: { avatar: WorkerAvatarLook }) {
      return crossAppBackendRequest('youAvatarUpdate', args);
    },

    youEmissionsMassUnitUpdate(args: { emissionsMassUnit: EmissionsMassUnitID }) {
      return crossAppBackendRequest('youEmissionsMassUnitUpdate', args);
    },

    userUpdate(args: {
      userID: UserID;
      name: string;
      email: Email;
      primaryTenantID: TenantID | null;
      primaryFacilityGroupID: FacilityGroupID | null;
      primaryFacilityID: FacilityID | null;
    }) {
      return crossAppBackendRequest('userUpdate', args);
    },

    listForManagement() {
      return crossAppBackendRequest('usersListForManagement', {});
    },

    async listForTenant(
      tenantID: TenantID,
    ): Promise<{ uid: Uid; email: Email; name: string; facilityName: string; primaryFacilityID: FacilityID | null }[]> {
      if (tenantID) {
        return crossAppBackendRequest('usersListForTenant', { tenantID });
      } else {
        return [];
      }
    },

    create(args: {
      name: string;
      email: Email;
      primaryTenantID: TenantID | null;
      primaryFacilityGroupID: FacilityGroupID | null;
      primaryFacilityID: FacilityID | null;
    }) {
      return crossAppBackendRequest('userCreate', args);
    },

    permissionAdd(args: {
      userID: UserID;
      permissionTypeID: PermissionTypeID;
      tenantID: TenantID | null;
      facilityGroupID: FacilityGroupID | null;
      facilityID: FacilityID | null;
    }) {
      return crossAppBackendRequest('userPermissionAdd', args);
    },

    permissionRemove(permissionID: PermissionID) {
      return crossAppBackendRequest('userPermissionRemove', { permissionID });
    },

    facilityPermissionSummary(args: { facilityID: FacilityID }) {
      return crossAppBackendRequest('facilityPermissionSummary', args);
    },

    ensureAccessOnTestSite() {
      return crossAppBackendRequest('userEnsureAccessOnTestSite', {});
    },

    createDemoTenantIfNecessary(): Promise<void> {
      return crossAppBackendRequest('userCreateDemoTenantIfNecessary', {});
    },
  },

  facilities: {
    list() {
      return crossAppBackendRequest('facilitiesList', {});
    },

    detailsGet(facilityID: FacilityID) {
      return crossAppBackendRequest('facilityDetailsGet', { facilityID });
    },

    detailsUpdate(args: Omit<SimpleSelectable<Facility>, 'timeZone'>) {
      return crossAppBackendRequest('facilityDetailsUpdate', args);
    },

    create(args: Omit<SimpleSelectable<Facility>, 'timeZone'>) {
      return crossAppBackendRequest('facilityCreate', args);
    },

    listGroups() {
      return crossAppBackendRequest('facilityGroupsList', {});
    },

    groupCreate(args: { tenantID: TenantID; groupName: string }) {
      return crossAppBackendRequest('facilityGroupCreate', args);
    },

    contactsForAllInTenant(tenantID: TenantID) {
      return crossAppBackendRequest('facilityContactsForAllInTenant', { tenantID });
    },
  },

  tenants: {
    create(args: TenantCreateDraft) {
      return crossAppBackendRequest('tenantCreate', args);
    },
  },

  appAccess: {
    appsList() {
      return crossAppBackendRequest('appsList', {});
    },

    overviewGet() {
      return crossAppBackendRequest('appAccessOverviewGet', {});
    },

    update(args: {
      appID: AppID;
      changes: { target: AppAccessTarget; isGrantedAccess: boolean | null }[];
      overridesBeneathClear: AppAccessTarget[];
      overridesBeneathKeep: AppAccessTarget[];
    }) {
      return crossAppBackendRequest('appAccessUpdate', args);
    },
  },

  platformStatus: {
    list(args: { tenantID: TenantID } | { facilityID: FacilityID }) {
      return crossAppBackendRequest('platformStatusList', args);
    },
  },
};
